/* Admin · Audit Logs — the prototype's immutable event log with KPI tiles, the filter
 * bar and the CSV export that states its row count before the download starts. */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { admin } from '../../api/client'
import type { AuditEntry, AuditListResponse } from '../../api/types'
import {
  Card,
  EmptyState,
  PageHead,
  Skeleton,
  errorMessage,
  nf,
  useToast,
} from '../../ui/chrome'
import { Icon } from '../../ui/icons'

function typePill(entry: AuditEntry) {
  const cls =
    entry.row_state === 'access_filtered'
      ? 'p-danger'
      : entry.action === 'query' || entry.action === 'ask'
        ? 'p-info'
        : entry.action === 'login' || entry.action === 'logout'
          ? 'p-grey'
          : 'p-lime'
  return <span className={`pill ${cls}`}>{entry.action}</span>
}

export default function AuditScreen() {
  const toast = useToast()

  const [data, setData] = useState<AuditListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [userUpn, setUserUpn] = useState('')
  /* "From" and "To" are dates; the API takes datetimes. A day is inclusive at both ends, so
   * the range covers 00:00:00 on the first day through 23:59:59 on the last — picking the
   * same date twice returns that whole day rather than nothing. */
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const range = useMemo(
    () => ({
      since: from ? `${from}T00:00:00` : undefined,
      until: to ? `${to}T23:59:59` : undefined,
    }),
    [from, to],
  )

  const refresh = useCallback(() => {
    admin
      .audit({
        user_upn: userUpn || undefined,
        since: range.since,
        until: range.until,
        limit: 200,
      })
      .then((response) => {
        setData(response)
        setError(null)
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [userUpn, range])

  useEffect(() => {
    const timer = setTimeout(refresh, userUpn ? 300 : 0)
    return () => clearTimeout(timer)
  }, [refresh, userUpn])

  const rows = useMemo(() => {
    const items = data?.items ?? []
    if (!q) return items
    const needle = q.toLowerCase()
    return items.filter((entry) =>
      [entry.query_text, entry.user_upn, entry.outcome, ...entry.document_labels]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    )
  }, [data, q])

  function doExport() {
    // The export must carry the same filters the table shows, date range included.
    const params = {
      user_upn: userUpn || undefined,
      since: range.since,
      until: range.until,
    }
    admin
      .auditExportPreview(params)
      .then((preview) => {
        toast('info', 'Preparing export', `${nf(preview.row_count)} rows match the current filters.`)
        window.location.href = admin.auditExportUrl(params)
      })
      .catch((cause: unknown) => toast('err', 'Export failed', errorMessage(cause)))
  }

  return (
    <>
      <PageHead
        title="Audit Logs"
        actions={
          <button type="button" className="btn btn-soft" onClick={doExport} disabled={!data}>
            <Icon name="dl" size={16} /> Export CSV
          </button>
        }
      />

      <div className="filters">
        <div className="field">
          <label htmlFor="auQ">Search</label>
          <input
            id="auQ"
            className="inp"
            placeholder="Detail, user or document"
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="auFrom">From</label>
          <input
            id="auFrom"
            type="date"
            className="inp"
            value={from}
            max={to || undefined}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="auTo">To</label>
          <input
            id="auTo"
            type="date"
            className="inp"
            value={to}
            min={from || undefined}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="auUser">User</label>
          <input
            id="auUser"
            className="inp"
            placeholder="name@kbl.co.in"
            value={userUpn}
            onChange={(event) => setUserUpn(event.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-soft"
          onClick={() => {
            setQ('')
            setFrom('')
            setTo('')
            setUserUpn('')
            toast('info', 'Filters cleared')
          }}
        >
          <Icon name="refresh" size={16} /> Reset
        </button>
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>The audit log could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      <Card
        icon="list"
        title="Event log"
        hint={data ? `${rows.length} matching · showing up to 120` : undefined}
        tight
      >
        {!data && !error ? (
          <Skeleton rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="list"
            title="No events match"
            msg="Adjust or clear the filters."
            action={
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setQ('')
                  setFrom('')
                  setTo('')
                  setUserUpn('')
                }}
              >
                Clear filters
              </button>
            }
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Event ID</th>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Detail</th>
                  <th>Documents</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 120).map((entry) => (
                  <tr key={entry.id}>
                    <td className="mono tiny nowrap">AUD-{entry.id}</td>
                    <td className="mono tiny nowrap">
                      {new Date(entry.occurred_at).toLocaleString('en-IN')}
                    </td>
                    <td>
                      <div className="flex aic gap8">
                        <span className="avatar-sm">
                          {(entry.user_upn ?? 'S').charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <strong className="tiny">{entry.user_upn ?? 'System'}</strong>
                          {entry.user_department && (
                            <div className="tiny muted">{entry.user_department}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{typePill(entry)}</td>
                    <td className="tiny">
                      {entry.query_text ?? '—'}
                      {entry.result_count !== null && (
                        <div className="muted">{entry.result_count} result(s)</div>
                      )}
                    </td>
                    <td className="tiny mono">
                      {entry.document_labels.length > 0 ? entry.document_labels.join(' | ') : '—'}
                    </td>
                    <td className="tiny">{entry.outcome ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
